/**
 * FR-1 – FR-5 — adding a receipt.
 *
 * Goal #1 is capture-to-archive in under 60 seconds, "or the habit dies", and
 * FR-2 says it has to work at the pharmacy counter rather than later at home.
 * Everything here follows from that:
 *
 *   - The camera is the first control and needs one tap.
 *   - The file goes straight to S3 (FR-3) and is downscaled first (FR-4), so a
 *     bad cell signal does not eat a 4 MB photo.
 *   - Nothing about the expense is asked for here. The upload is the whole
 *     job; reading and review happen afterwards.
 *   - FR-5's bulk mode is the same code path with `multiple`.
 *
 * Each upload gets its own row, keyed by an id made here. Rows used to be
 * addressed by position, read from state that had not updated yet, so every
 * file in a batch wrote to the same row.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, uploadReceipt, ApiError } from '../api.js';
import { Icon, Money, PageHead, formatDate } from '../ui.js';
import type { ExpenseDetailResponse } from '../types.js';

interface Row {
  /** Local id for this upload. Not the expense id, which arrives later. */
  key: string;
  name: string;
  status: 'uploading' | 'reading' | 'ready' | 'slow' | 'failed';
  expenseId?: string;
  error?: string;
  summary?: { provider: string | null; amount: number | null; date: string | null };
}

/** The reader usually finishes in well under a minute. */
const POLL_MS = 4000;
const POLL_LIMIT = 30;

let rowSeq = 0;

export function Add() {
  const [rows, setRows] = useState<Row[]>([]);
  const [dragging, setDragging] = useState(false);
  const cameraRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLInputElement>(null);
  const alive = useRef(true);
  useEffect(() => () => void (alive.current = false), []);

  const update = useCallback((key: string, patch: Partial<Row>) => {
    if (alive.current) setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  /**
   * The record does not exist until the reader has finished with the image, so
   * a link shown straight after upload used to land on "not found". Wait for
   * it here and show what was read.
   */
  const waitForRead = useCallback(
    async (key: string, expenseId: string) => {
      for (let attempt = 0; attempt < POLL_LIMIT && alive.current; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
        try {
          const { expense } = await api.get<ExpenseDetailResponse>(`/api/expenses/${expenseId}`);
          update(key, {
            status: 'ready',
            summary: {
              provider: expense.provider_name,
              amount: expense.amount_claimed_cents,
              date: expense.date_of_service,
            },
          });
          return;
        } catch (err) {
          if (!(err instanceof ApiError) || err.status !== 404) break;
        }
      }
      update(key, { status: 'slow' });
    },
    [update],
  );

  const handle = useCallback(
    async (list: FileList | null) => {
      if (!list || list.length === 0) return;
      const files = Array.from(list);
      const fresh = files.map((file) => ({
        key: `u${++rowSeq}`,
        name: file.name,
        status: 'uploading' as const,
      }));
      setRows((prev) => [...fresh, ...prev]);

      // One at a time: a phone on a weak signal does better with one upload
      // finishing than with five competing.
      for (const [i, file] of files.entries()) {
        const key = fresh[i]!.key;
        try {
          const { expenseId } = await uploadReceipt(file);
          update(key, { status: 'reading', expenseId });
          void waitForRead(key, expenseId);
        } catch (err) {
          update(key, { status: 'failed', error: err instanceof Error ? err.message : String(err) });
        }
      }
    },
    [update, waitForRead],
  );

  return (
    <>
      <PageHead title="Add a receipt">Take the photo now. The details are read for you.</PageHead>

      {/* `capture="environment"` opens the rear camera directly rather than a
          picker. On a phone this is the difference between one tap and three. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          void handle(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={pickerRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        hidden
        onChange={(e) => {
          void handle(e.target.files);
          e.target.value = '';
        }}
      />

      <div
        className={`drop${dragging ? ' over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void handle(e.dataTransfer.files);
        }}
      >
        <button className="primary big" onClick={() => cameraRef.current?.click()}>
          <Icon name="camera" />
          Take a photo
        </button>
        <button className="big" onClick={() => pickerRef.current?.click()}>
          <Icon name="upload" />
          Choose files
        </button>
        <p className="xs muted">Photos and PDFs. Several at once is fine.</p>
      </div>

      {rows.length > 0 ? (
        <div className="list" aria-live="polite">
          {rows.map((row) => (
            <UploadRow key={row.key} row={row} />
          ))}
        </div>
      ) : null}

      <h2 className="section">
        <span>Other things to add</span>
      </h2>
      <div className="list">
        <Link className="item" to="/mileage">
          <span className="tile">
            <Icon name="car" />
          </span>
          <span>
            <div className="title">A drive to medical care</div>
            <div className="sub">Miles, parking and tolls</div>
          </span>
          <span className="end muted">
            <Icon name="chevron" />
          </span>
        </Link>
        <Link className="item" to="/imports">
          <span className="tile">
            <Icon name="cart" />
          </span>
          <span>
            <div className="title">Amazon orders</div>
            <div className="sub">Once a year, from an export</div>
          </span>
          <span className="end muted">
            <Icon name="chevron" />
          </span>
        </Link>
      </div>
    </>
  );
}

function UploadRow({ row }: { row: Row }) {
  const body = (
    <>
      <span className="tile">
        <Icon name="receipt" />
      </span>
      <span>
        <div className="title">{row.summary?.provider ?? row.name}</div>
        <div className="sub">
          {row.status === 'uploading' ? (
            'Uploading…'
          ) : row.status === 'reading' ? (
            'Saved. Reading the details…'
          ) : row.status === 'failed' ? (
            <span className="tone-stop">Not saved: {row.error ?? 'upload failed'}</span>
          ) : row.status === 'slow' ? (
            'Saved. Still reading; it will be on Home shortly.'
          ) : (
            <>Saved · {formatDate(row.summary?.date)}</>
          )}
        </div>
      </span>
      <span className="end">
        {row.status === 'ready' ? (
          <>
            <Money cents={row.summary?.amount} />
            <div className="sub tone-ok">Review</div>
          </>
        ) : null}
      </span>
    </>
  );
  return row.status === 'ready' && row.expenseId ? (
    <Link className="item" to={`/expenses/${row.expenseId}`}>
      {body}
    </Link>
  ) : (
    <div className="item">{body}</div>
  );
}
